import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Send,
  CheckCircle2,
  Loader2,
  AlertCircle,
  ArrowRight,
  Sparkles,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const BUSINESS_TYPES = [
  "HVAC",
  "Plumbing",
  "Electrical",
  "Roofing",
  "Pool Cage / Screen Repair",
  "Remodeling",
  "Other Home Service",
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function fieldClass(hasError) {
  return `w-full rounded-xl border bg-white px-3.5 py-3 text-sm text-navy placeholder:text-navy/40 transition-colors focus:outline-none focus:ring-2 focus:ring-aqua/60 ${
    hasError ? "border-red-400" : "border-mist-line"
  }`;
}

function Label({ htmlFor, required, children, hint }) {
  return (
    <div className="mb-1.5 flex items-baseline justify-between gap-3">
      <label
        htmlFor={htmlFor}
        className="text-[13px] font-semibold text-white"
      >
        {children}
        {required && <span className="ml-1 text-sun">*</span>}
      </label>
      {hint && (
        <span className="text-[11px] font-medium text-white/55">{hint}</span>
      )}
    </div>
  );
}

function FieldError({ id, message }) {
  if (!message) return null;
  return (
    <p
      id={id}
      role="alert"
      className="mt-1.5 flex items-center gap-1 text-xs font-medium text-red-300"
    >
      <AlertCircle className="h-3 w-3" />
      {message}
    </p>
  );
}

export default function ContractorInquiryForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    businessType: "",
    phone: "",
    notes: "",
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | submitting | success
  const successHeadingRef = useRef(null);
  const formRef = useRef(null);

  const update = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Please enter your name.";
    if (!form.email.trim()) next.email = "Please enter your email.";
    else if (!EMAIL_RE.test(form.email.trim()))
      next.email = "Please enter a valid email address.";
    if (!form.company.trim()) next.company = "Please enter your company name.";
    if (!form.businessType) next.businessType = "Pick the closest match.";
    setErrors(next);
    return next;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    // Locally-computed errors — `errors` state isn't updated synchronously,
    // so reading it right after `setErrors` would be stale.
    const nextErrors = validate();
    if (Object.keys(nextErrors).length > 0) {
      // focus first error
      const firstKey = ["name", "email", "company", "businessType"].find(
        (k) => nextErrors[k]
      );
      if (firstKey) {
        const el = formRef.current?.querySelector(`[name="${firstKey}"]`);
        el?.focus();
      }
      return;
    }
    setStatus("submitting");
    try {
      // Phase 3 — endpoint is real; if it ever 404s we still surface the
      // confirmation card so the demo doesn't break visibly.
      const payload = {
        full_name: form.name.trim(),
        email: form.email.trim(),
        company_name: form.company.trim(),
        business_type: form.businessType,
        phone: form.phone.trim() || null,
        notes: form.notes.trim() || null,
      };
      await fetch(`${API_URL}/api/contractor-inquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }).catch(() => null);
    } catch {
      /* swallow — graceful fallback to client-only confirmation */
    }
    setStatus("success");
    // move focus to the confirmation heading for screen readers
    requestAnimationFrame(() => {
      successHeadingRef.current?.focus();
    });
  };

  const reset = () => {
    setForm({
      name: "",
      email: "",
      company: "",
      businessType: "",
      phone: "",
      notes: "",
    });
    setErrors({});
    setStatus("idle");
  };

  const firstName = form.name.trim().split(" ")[0] || "there";

  return (
    <div
      data-testid="contractor-inquiry-card"
      className="relative overflow-hidden rounded-3xl border border-sun/40 bg-navy p-1.5 shadow-lift"
    >
      <div className="absolute -right-20 -top-20 h-56 w-56 rounded-full bg-aqua/25 blur-3xl" aria-hidden="true" />
      <div className="absolute -bottom-24 -left-16 h-64 w-64 rounded-full bg-sun/20 blur-3xl" aria-hidden="true" />

      <div className="relative rounded-[1.4rem] bg-navy-deep/95 p-6 text-white backdrop-blur sm:p-9">
        <AnimatePresence mode="wait" initial={false}>
          {status === "success" ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.35, ease: "easeOut" }}
              data-testid="contractor-inquiry-success"
            >
              <span className="inline-flex items-center gap-1.5 rounded-full border border-aqua/40 bg-aqua/15 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-aqua">
                <CheckCircle2 className="h-3 w-3" />
                Inquiry received
              </span>
              <h3
                ref={successHeadingRef}
                tabIndex={-1}
                className="mt-4 font-display text-3xl font-bold leading-tight text-white sm:text-4xl"
              >
                Thanks, {firstName}.
              </h3>
              <p className="mt-3 max-w-lg text-base text-white/80">
                PrismForge Digital will be in touch shortly about building
                something similar for your business.
              </p>
              <p className="mt-3 max-w-lg text-xs text-white/55">
                Demo capture &mdash; your inquiry will be saved as a sample
                submission for PrismForge Digital review.
              </p>
              <div className="relative z-30 mt-7 flex flex-wrap items-center gap-3">
                <a
                  href="https://prismforgedigital.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contractor-inquiry-success-link"
                  className="inline-flex items-center gap-2 rounded-full bg-sun px-5 py-3 text-sm font-semibold text-navy-deep shadow-sunGlow transition-transform hover:scale-[1.02]"
                >
                  Visit PrismForge Digital
                  <ArrowRight className="h-4 w-4" />
                </a>
                <button
                  type="button"
                  onClick={reset}
                  data-testid="contractor-inquiry-reset"
                  className="text-sm font-semibold text-white/80 underline-offset-4 hover:text-aqua hover:underline"
                >
                  Send another message
                </button>
              </div>
            </motion.div>
          ) : (
            <motion.form
              key="form"
              ref={formRef}
              onSubmit={onSubmit}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              noValidate
              data-testid="contractor-inquiry-form"
              aria-describedby="contractor-form-notice"
            >
              <span className="inline-flex items-center gap-1.5 rounded-full border border-sun/40 bg-sun/15 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-sun">
                <Sparkles className="h-3 w-3" />
                For HVAC &amp; Home Service Owners
              </span>
              <h3 className="mt-4 font-display text-3xl font-bold leading-tight text-white sm:text-4xl">
                Want a system like this for your HVAC company?
              </h3>
              <p className="mt-3 max-w-lg text-base text-white/80">
                Drop your email and PrismForge Digital can send you a short
                walkthrough or follow up about building something similar.
              </p>
              <p
                id="contractor-form-notice"
                className="mt-4 rounded-xl border border-white/10 bg-white/[0.04] p-3 text-xs leading-relaxed text-white/70"
              >
                This form goes to <span className="font-semibold text-white">PrismForge Digital</span> &mdash; it&rsquo;s not a Sun Chaser Cooling
                service request and not an HVAC emergency dispatch.
              </p>

              <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="ci-name" required>Full name</Label>
                  <input
                    id="ci-name"
                    name="name"
                    type="text"
                    autoComplete="name"
                    required
                    aria-required="true"
                    aria-invalid={!!errors.name}
                    aria-describedby={errors.name ? "ci-name-err" : undefined}
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Jordan Carter"
                    className={fieldClass(errors.name)}
                    data-testid="contractor-input-name"
                  />
                  <FieldError id="ci-name-err" message={errors.name} />
                </div>
                <div>
                  <Label htmlFor="ci-email" required>Email</Label>
                  <input
                    id="ci-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    aria-required="true"
                    aria-invalid={!!errors.email}
                    aria-describedby={errors.email ? "ci-email-err" : undefined}
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@yourcompany.com"
                    className={fieldClass(errors.email)}
                    data-testid="contractor-input-email"
                  />
                  <FieldError id="ci-email-err" message={errors.email} />
                </div>
                <div>
                  <Label htmlFor="ci-company" required>Company name</Label>
                  <input
                    id="ci-company"
                    name="company"
                    type="text"
                    autoComplete="organization"
                    required
                    aria-required="true"
                    aria-invalid={!!errors.company}
                    aria-describedby={errors.company ? "ci-company-err" : undefined}
                    value={form.company}
                    onChange={update("company")}
                    placeholder="Cool Coast HVAC"
                    className={fieldClass(errors.company)}
                    data-testid="contractor-input-company"
                  />
                  <FieldError id="ci-company-err" message={errors.company} />
                </div>
                <div>
                  <Label htmlFor="ci-businessType" required>Business type</Label>
                  <select
                    id="ci-businessType"
                    name="businessType"
                    required
                    aria-required="true"
                    aria-invalid={!!errors.businessType}
                    aria-describedby={errors.businessType ? "ci-bt-err" : undefined}
                    value={form.businessType}
                    onChange={update("businessType")}
                    className={fieldClass(errors.businessType)}
                    data-testid="contractor-input-business-type"
                  >
                    <option value="">Select one…</option>
                    {BUSINESS_TYPES.map((b) => (
                      <option key={b} value={b}>
                        {b}
                      </option>
                    ))}
                  </select>
                  <FieldError id="ci-bt-err" message={errors.businessType} />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="ci-phone" hint="optional">Phone</Label>
                  <input
                    id="ci-phone"
                    name="phone"
                    type="tel"
                    autoComplete="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="(555) 000-0000"
                    className={fieldClass(false)}
                    data-testid="contractor-input-phone"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="ci-notes" hint="optional">
                    Notes &mdash; what do you want help with?
                  </Label>
                  <textarea
                    id="ci-notes"
                    name="notes"
                    rows={4}
                    value={form.notes}
                    onChange={update("notes")}
                    placeholder="Custom website, smart intake form, missed-call follow-up, CRM setup, etc."
                    className={fieldClass(false)}
                    data-testid="contractor-input-notes"
                  />
                </div>
              </div>

              <div className="mt-7 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-[11px] text-white/55">
                  By submitting, you agree PrismForge Digital can email you
                  about this inquiry. Demo capture only.
                </p>
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  data-testid="contractor-inquiry-submit"
                  className="group inline-flex items-center justify-center gap-2 rounded-full bg-sun px-6 py-3.5 text-sm font-semibold text-navy-deep shadow-sunGlow transition-transform hover:scale-[1.02] disabled:cursor-wait disabled:opacity-80"
                >
                  {status === "submitting" ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Sending…
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      Send to PrismForge Digital
                    </>
                  )}
                </button>
              </div>
            </motion.form>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
